import { useState, useEffect, useCallback } from 'react'
import { FiSlash, FiLoader, FiPlus, FiRotateCcw, FiAlertTriangle, FiUser, FiAward, FiTrash2 } from 'react-icons/fi'
import { useAuth } from '../contexts/AuthContext'
import { API_ENDPOINTS } from '../lib/api'
import ConfirmDialog from './ConfirmDialog'
import { LideresBloqueadasSection } from './LideresBloqueadasSection'

/**
 * Painel Bloqueados — duas listas independentes, em sub-abas:
 *  - Contatos: golpistas que a Jú ignora (esta seção, origem abaixo).
 *  - Líderes:  quem não recebe lead (LideresBloqueadasSection, 17/07/2026).
 * São coisas diferentes — uma bloqueia quem CHEGA, a outra quem RECEBE — mas
 * moram na mesma aba porque a pergunta da operação é a mesma: "quem está fora".
 *
 * Origem (16/07/2026): o contato Guiomar Gonçalves da Silva (67 99860-3533)
 * aplicava golpes em Três Lagoas, entrou pelo tráfego pago e foi encaminhado a
 * uma líder pelo fluxo normal antes da diretoria identificar. Bloquear aqui faz
 * três coisas no backend: o bot para de responder, o auto-assign nunca mais
 * encaminha o número a uma líder, e a conversa ganha um badge vermelho na inbox
 * de todos os operadores.
 *
 * Match é por telefone (DDD + 8 dígitos finais) — ver lib/contact-blocklist.cjs.
 */

interface BlockEntry {
  id: number
  phone_key: string | null
  phone_e164: string | null
  contact_name: string | null
  reason: string
  blocked_by: string
  blocked_at: string | null
  active: number
  unblocked_by: string | null
  unblocked_at: string | null
  hit_count: number
  last_hit_at: string | null
}

// "5567998603533" → "(67) 99860-3533"
function fmtPhone(e164: string | null): string {
  if (!e164) return '—'
  const d = e164.replace(/\D/g, '').replace(/^55/, '')
  if (d.length < 10) return e164
  const ddd = d.slice(0, 2)
  const rest = d.slice(2)
  const meio = rest.length === 9 ? rest.slice(0, 5) : rest.slice(0, 4)
  const fim = rest.length === 9 ? rest.slice(5) : rest.slice(4)
  return `(${ddd}) ${meio}-${fim}`
}

function fmtDate(s: string | null): string {
  if (!s) return '—'
  const iso = s.includes('T') ? s : s.replace(' ', 'T') + 'Z'
  const d = new Date(iso)
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
}

export function BloqueadosPanel() {
  const { authenticatedFetch } = useAuth()
  const [entries, setEntries] = useState<BlockEntry[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [phone, setPhone] = useState('')
  const [name, setName] = useState('')
  const [reason, setReason] = useState('')
  const [confirm, setConfirm] = useState<{ isOpen: boolean; entry: BlockEntry | null }>({ isOpen: false, entry: null })
  const [tab, setTab] = useState<'contatos' | 'lideres'>('contatos')
  const [confirmDel, setConfirmDel] = useState<{ isOpen: boolean; entry: BlockEntry | null }>({ isOpen: false, entry: null })

  const load = useCallback(async () => {
    setError(null)
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.BLOCKLIST)
      const d = await r.json()
      if (!r.ok || !d.success) throw new Error(d.error || `HTTP ${r.status}`)
      setEntries(d.entries)
    } catch (e) {
      setError((e as Error).message)
      setEntries([])
    }
  }, [authenticatedFetch])

  useEffect(() => { load() }, [load])

  const submit = async () => {
    if (!phone.trim() || !reason.trim()) return
    setSaving(true)
    setError(null)
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.BLOCKLIST, {
        method: 'POST',
        body: JSON.stringify({ phone, name, reason }),
      })
      const d = await r.json()
      if (!r.ok || !d.success) throw new Error(d.error || `HTTP ${r.status}`)
      setPhone(''); setName(''); setReason(''); setShowForm(false)
      await load()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  const unblock = async (entry: BlockEntry) => {
    setError(null)
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.BLOCKLIST_ITEM(entry.id), { method: 'DELETE' })
      const d = await r.json()
      if (!r.ok || !d.success) throw new Error(d.error || `HTTP ${r.status}`)
      await load()
    } catch (e) {
      setError((e as Error).message)
    }
  }

  // Remoção definitiva: só pra quem já foi desbloqueado (o backend recusa 409 em
  // bloqueio ativo — sumir da lista sem rastro seria o pior caso).
  const removeFromList = async (entry: BlockEntry) => {
    setError(null)
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.BLOCKLIST_ITEM_PERMANENT(entry.id), { method: 'DELETE' })
      const d = await r.json()
      if (!r.ok || !d.success) throw new Error(d.error || `HTTP ${r.status}`)
      await load()
    } catch (e) {
      setError((e as Error).message)
    }
  }

  const ativos = entries?.filter(e => e.active) ?? []
  const inativos = entries?.filter(e => !e.active) ?? []

  return (
    <div className="glass-panel neu-bar neu-forms rounded-2xl p-4 h-full flex flex-col min-h-0">
      {/* Sub-abas: contatos (quem chega) × líderes (quem recebe) */}
      <div className="flex items-center gap-1 mb-3 shrink-0 border-b border-border/50">
        {([
          { id: 'contatos' as const, label: 'Contatos', icon: FiUser, hint: 'Golpistas e contatos que a Jú deve ignorar' },
          { id: 'lideres' as const, label: 'Líderes', icon: FiAward, hint: 'Líderes que não devem receber novos leads' },
        ]).map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            title={t.hint}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium border-b-2 -mb-px transition-colors ${
              tab === t.id
                ? 'border-red-500 text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <t.icon className="w-3.5 h-3.5" />
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'lideres' && (
        <div className="flex-1 min-h-0">
          <LideresBloqueadasSection />
        </div>
      )}

      {tab === 'contatos' && (
      <>
      {/* Header */}
      <div className="flex items-center gap-2 mb-3 shrink-0">
        <FiSlash className="w-4 h-4 text-red-500 shrink-0" />
        <span className="text-sm font-semibold text-foreground flex-1">Contatos bloqueados</span>
        <span className="text-xs text-muted-foreground tabular-nums" title="Bloqueios ativos">
          {ativos.length} {ativos.length === 1 ? 'ativo' : 'ativos'}
        </span>
        <button
          onClick={() => setShowForm(v => !v)}
          title="Bloquear um novo número"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium bg-primary text-primary-foreground neu-relief hover:opacity-90 shrink-0"
        >
          <FiPlus className="w-4 h-4" />
          <span className="hidden sm:inline">Bloquear</span>
        </button>
      </div>

      <p className="text-[11px] text-muted-foreground mb-3 shrink-0 leading-relaxed">
        Um número bloqueado deixa de receber respostas da Jú e nunca é encaminhado a uma líder pelo
        auto-assign. As mensagens dele continuam chegando na inbox, marcadas em vermelho, para vocês
        acompanharem.
      </p>

      {error && (
        <div className="mb-2 px-3 py-2 rounded-lg bg-destructive/10 text-destructive text-xs shrink-0">{error}</div>
      )}

      {/* Formulário */}
      {showForm && (
        <div className="mb-3 p-3 rounded-xl border border-border/60 bg-muted/30 shrink-0 space-y-2">
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="Telefone com DDD — ex: (67) 99860-3533"
              title="Telefone do contato a bloquear, com DDD"
              className="flex-1 px-3 py-1.5 rounded-lg bg-background border border-border text-sm text-foreground placeholder:text-muted-foreground"
            />
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Nome (opcional)"
              title="Nome do contato, para identificar na lista"
              className="flex-1 px-3 py-1.5 rounded-lg bg-background border border-border text-sm text-foreground placeholder:text-muted-foreground"
            />
          </div>
          <input
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Motivo do bloqueio (obrigatório) — ex: aplica golpes em Três Lagoas"
            title="O motivo fica registrado para auditoria"
            className="w-full px-3 py-1.5 rounded-lg bg-background border border-border text-sm text-foreground placeholder:text-muted-foreground"
          />
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => { setShowForm(false); setError(null) }}
              className="px-3 py-1.5 rounded-lg text-[12px] font-medium text-muted-foreground hover:text-foreground"
            >
              Cancelar
            </button>
            <button
              onClick={submit}
              disabled={saving || !phone.trim() || !reason.trim()}
              title={!phone.trim() || !reason.trim() ? 'Telefone e motivo são obrigatórios' : 'Confirmar bloqueio'}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium bg-red-600 hover:bg-red-700 text-white neu-relief disabled:opacity-60"
            >
              {saving ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiSlash className="w-4 h-4" />}
              {saving ? 'Bloqueando…' : 'Bloquear'}
            </button>
          </div>
        </div>
      )}

      {/* Lista */}
      <div className="flex-1 min-h-0 overflow-y-auto rounded-xl border border-dashed border-border/60">
        {entries === null && (
          <div className="flex items-center justify-center gap-2 py-10 text-muted-foreground text-sm">
            <FiLoader className="w-4 h-4 animate-spin" /> Carregando…
          </div>
        )}
        {entries && entries.length === 0 && (
          <div className="py-10 text-center text-muted-foreground text-sm px-6">
            Nenhum contato bloqueado.
          </div>
        )}
        {entries && entries.length > 0 && (
          <ul className="space-y-1.5 py-1">
            {[...ativos, ...inativos].map(e => (
              <li
                key={e.id}
                className={`neu-item flex items-center gap-3 px-3 py-2.5 transition-colors ${e.active ? '' : 'opacity-50'}`}
              >
                <FiSlash
                  className={`w-4 h-4 shrink-0 ${e.active ? 'text-red-500' : 'text-muted-foreground'}`}
                  title={e.active ? 'Bloqueio ativo' : 'Desbloqueado'}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm text-foreground truncate font-medium">
                      {e.contact_name || 'Sem nome'}
                    </span>
                    <span className="text-xs text-muted-foreground tabular-nums shrink-0">
                      {fmtPhone(e.phone_e164)}
                    </span>
                    {e.hit_count > 0 && (
                      <span
                        className="shrink-0 inline-flex items-center gap-1 text-[9px] px-1 py-0.5 rounded neu-tag bg-red-500/15 text-red-600 dark:text-red-400 border border-red-500/40 font-bold whitespace-nowrap cursor-help"
                        title={`Tentou nos contatar ${e.hit_count}× depois de bloqueado. Última vez: ${fmtDate(e.last_hit_at)}`}
                      >
                        <FiAlertTriangle className="w-2.5 h-2.5" />
                        {e.hit_count}× voltou
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate" title={e.reason}>
                    {e.reason}
                  </div>
                  <div className="text-[10px] text-muted-foreground/70 truncate">
                    {e.active
                      ? `Bloqueado por ${e.blocked_by} em ${fmtDate(e.blocked_at)}`
                      : `Desbloqueado por ${e.unblocked_by} em ${fmtDate(e.unblocked_at)}`}
                  </div>
                </div>
                {e.active === 1 ? (
                  <button
                    onClick={() => setConfirm({ isOpen: true, entry: e })}
                    title="Desbloquear este contato"
                    className="p-1.5 rounded-lg neu-convex text-muted-foreground hover:text-foreground hover:bg-muted/60 shrink-0"
                  >
                    <FiRotateCcw className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={() => setConfirmDel({ isOpen: true, entry: e })}
                    title="Remover da lista definitivamente (o histórico deste bloqueio some)"
                    className="p-1.5 rounded-lg neu-convex text-muted-foreground hover:text-destructive hover:bg-destructive/10 shrink-0"
                  >
                    <FiTrash2 className="w-4 h-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirm.isOpen}
        type="warning"
        title="Desbloquear contato?"
        message={
          confirm.entry
            ? `${confirm.entry.contact_name || fmtPhone(confirm.entry.phone_e164)} volta a ser atendido pela Jú e pode ser encaminhado a uma líder pelo auto-assign.`
            : ''
        }
        details={confirm.entry ? [`Motivo do bloqueio: ${confirm.entry.reason}`] : undefined}
        confirmText="Desbloquear"
        cancelText="Manter bloqueado"
        onConfirm={() => {
          if (confirm.entry) unblock(confirm.entry)
          setConfirm({ isOpen: false, entry: null })
        }}
        onCancel={() => setConfirm({ isOpen: false, entry: null })}
      />

      <ConfirmDialog
        isOpen={confirmDel.isOpen}
        type="danger"
        title="Remover da lista?"
        message={
          confirmDel.entry
            ? `O registro de ${confirmDel.entry.contact_name || fmtPhone(confirmDel.entry.phone_e164)} some do painel para sempre. Ele já está desbloqueado — isto não muda o atendimento, só apaga o histórico.`
            : ''
        }
        details={
          confirmDel.entry
            ? [
                `Motivo registrado: ${confirmDel.entry.reason}`,
                `Bloqueado por ${confirmDel.entry.blocked_by}, desbloqueado por ${confirmDel.entry.unblocked_by || '—'}.`,
              ]
            : undefined
        }
        confirmText="Remover"
        cancelText="Manter no histórico"
        onConfirm={() => {
          if (confirmDel.entry) removeFromList(confirmDel.entry)
          setConfirmDel({ isOpen: false, entry: null })
        }}
        onCancel={() => setConfirmDel({ isOpen: false, entry: null })}
      />
      </>
      )}
    </div>
  )
}
